import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModelStatus } from "../../../hooks/useModelStatus";
import { useTenantPermission } from "../../../hooks/useTenantPermission";
import { modelReviewCopy, modelStatusCopy as copy } from "../../../services/modelling/modelStatus.copy";
import { toModelStatusBanner, toModelStatusNote, toReviewableRunId } from "../../../services/modelling/modelStatusMappers";
import type { StatusBannerProps } from "../../molecules/StatusBanner/StatusBanner.types";
import type { ModelReviewDialogProps } from "../ModelReviewDialog";
import type { ModelStatusPlacement } from "./ModelStatusBanner.types";

const SOURCES_PATH = "/data-connectors";

/** Only a tenant admin holds it: they approve or reject a held update. */
export const MODEL_CURATE_PERMISSION = "model:curate";

export interface ModelStatusBannerState {
  /** Null when there is nothing to say here. */
  banner: StatusBannerProps | null;
  review: ModelReviewDialogProps;
}

/** The banner's props, plus the review dialog a tenant admin opens from a held update. */
export const useModelStatusBannerState = (placement: ModelStatusPlacement): ModelStatusBannerState => {
  const navigate = useNavigate();
  const { status, errorMessage, retry } = useModelStatus();
  const { allowed: canReview } = useTenantPermission(MODEL_CURATE_PERMISSION);
  const [reviewing, setReviewing] = useState<string | null>(null);
  const reviewableRunId = placement === "page" ? toReviewableRunId(status, canReview) : null;
  const review: ModelReviewDialogProps = { runId: reviewing, onClose: () => setReviewing(null) };

  if (errorMessage) {
    const banner: StatusBannerProps | null =
      placement === "page"
        ? { tone: "error", title: errorMessage, actionItems: [{ id: "retry", label: copy.retry, onClick: retry }] }
        : null;
    return { banner, review };
  }
  const notice = placement === "page" ? toModelStatusBanner(status) : toModelStatusNote(status);
  if (!notice) return { banner: null, review };
  const actionItems = notice.linksToSources
    ? [{ id: "sources", label: copy.dataSources, onClick: () => navigate(SOURCES_PATH) }]
    : reviewableRunId
      ? [{ id: "review", label: modelReviewCopy.review, onClick: () => setReviewing(reviewableRunId) }]
      : undefined;
  return {
    banner: {
      tone: notice.tone,
      leading: notice.isBuilding ? "spinner" : undefined,
      title: notice.title,
      detail: notice.detail ?? undefined,
      actionItems,
    },
    review,
  };
};
